import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { AudioPlayer } from "../components/AudioPlayer";
import { PrayerDiary } from "../components/PrayerDiary";
import { ShareOracao } from "../components/ShareOracao";
import { LeafBranch, SmallSprig } from "../components/Botanical";
import { Heart, ArrowLeft } from "lucide-react";

export default function PrayerPage() {
  const { number } = useParams();
  const location = useLocation();
  const fromIndex = location.state?.fromIndex;
  const num = parseInt(number, 10);
  const [prayer, setPrayer] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!num || num < 1 || num > 50) {
      setNotFound(true);
      return;
    }
    setPrayer(null);
    setNotFound(false);
    api
      .get(`/prayers/${num}`)
      .then((res) => setPrayer(res.data))
      .catch(() => setNotFound(true));
  }, [num]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="font-serif-display text-3xl text-[#3A2921]">Oração não encontrada</p>
        <p className="mt-3 text-sm text-[#9C877A]">Verifique o QR Code do seu e-book e tente novamente.</p>
      </div>
    );
  }

  const audioSrc = prayer ? `${api.defaults.baseURL}/prayers/${num}/audio` : null;

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Botanical decorations */}
      <LeafBranch className="pointer-events-none absolute -top-4 -left-6 w-28 sm:w-36 opacity-25" />
      <LeafBranch flip className="pointer-events-none absolute -bottom-6 -right-6 w-28 sm:w-36 opacity-25" />

      <div className="max-w-xl mx-auto px-5 py-9 sm:py-12 flex flex-col items-center min-h-screen justify-between relative z-10">
        {fromIndex && (
          <Link
            to="/oracoes"
            data-testid="back-to-list-button"
            className="self-start inline-flex items-center gap-1.5 text-sm text-[#6E5A4F] hover:text-[#3A2921] transition-colors mb-4 -mt-2"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar às orações
          </Link>
        )}
        {/* Masthead */}
        <header className="w-full text-center border-b border-[#E5DCCF]/70 pb-6 mb-8 fade-up">
          <div data-testid="masthead-home-link">
            <h1
              data-testid="masthead-title"
              className="font-cinzel uppercase text-[#3A2921] tracking-[0.22em] text-sm sm:text-base"
            >
              Quando Faltam Palavras
            </h1>
            <p
              data-testid="masthead-subtitle"
              className="font-serif-display italic text-[#9C877A] text-sm sm:text-base mt-2 tracking-wide"
            >
              50 Orações para Falar com Deus
            </p>
          </div>
        </header>

        {/* Prayer card */}
        <main className="w-full flex flex-col items-center flex-1 justify-center">
          <div className="w-full bg-[#FAF7F2] rounded-3xl p-6 sm:p-9 border border-[#E5DCCF] shadow-sm relative overflow-hidden fade-up delay-1">
            <SmallSprig className="absolute top-5 right-5 w-14 opacity-30" />

            <span
              data-testid="prayer-number-badge"
              className="font-cinzel uppercase tracking-[0.25em] text-[#C5A059] text-xs font-semibold block text-center mb-3"
            >
              Oração {String(num).padStart(2, "0")}
            </span>

            {prayer ? (
              <h2
                data-testid="prayer-title"
                className="font-serif-display text-3xl sm:text-4xl text-[#3A2921] font-semibold leading-tight text-center mb-8"
              >
                {prayer.title}
              </h2>
            ) : (
              <div className="h-10 w-2/3 mx-auto bg-[#EFE7DA] rounded animate-pulse mb-8" />
            )}

            <AudioPlayer src={audioSrc} hasAudio={!!prayer?.has_audio} />
          </div>

          {prayer && <PrayerDiary number={num} />}

          {prayer && <ShareOracao number={num} title={prayer.title} />}

          {/* Devotional quote */}
          <p
            data-testid="devotional-quote"
            className="font-serif-display italic text-base sm:text-lg text-[#6E5A4F] mt-8 mb-2 px-4 text-center flex items-center justify-center gap-3 fade-up delay-2"
          >
            <SmallSprig className="w-8 opacity-40 hidden sm:block" />
            Respire fundo. Este é o seu momento com Deus.
            <SmallSprig className="w-8 opacity-40 hidden sm:block" style={{ transform: "scaleX(-1)" }} />
          </p>
        </main>

        <footer className="mt-8 flex items-center gap-1.5 text-[#C5A059]/70 text-xs">
          <Heart className="w-3 h-3" fill="currentColor" strokeWidth={0} />
          <span className="font-serif-display italic">feito com fé</span>
        </footer>
      </div>
    </div>
  );
}
